import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../../components/app/AppToast";
import { loginWithApple, loginWithGoogle } from "../../../services/firebase";
import { classifyEmailForAuth } from "../checkEmailOutcome";
import { memorizePostLoginRedirectTo } from "../../app/utils/postLoginRedirect";
import {
  gaEventAuthMethodSelected,
  type SignupSurface,
} from "../../../tools/analytics/domains/onboarding.analytics";

/**
 * `LoginSheet`: whether it is up, and what it does with what the user gives it.
 *
 * The sheet has three hosts — the `/welcome` front door, the `/login` page, and
 * the shared-conversation reader — and the three used to carry their own copy
 * of this. The copies drifted: the shared-conversation one called
 * `/webapp/public/auth/check-email` directly with its own `z.email()` guard, so
 * it could not tell an unusable address from a check that never came back, and
 * showed the "something went wrong" toast for both.
 *
 * The one branch that differs per host is the one parameter. `onExistingAccount`
 * exists because this sheet is the SIGNUP door, so an address that already has
 * an account has to be handed over: `/welcome` and the shared reader push
 * `/login`, while `/login` is already there and only fills its own form.
 *
 * Everything else is the same everywhere. A new address goes to the onboarding
 * carousel with the address in hand, so the carousel can open on the code slide.
 *
 * `returnTo` is the page a provider login lands back on (see `loginAndRedirect`):
 * the popup keeps the host on screen, so the host says which page it is. A new
 * address carries it through onboarding too, which opens it at the end
 * (`finishOnboarding`): the reader signed up to read that page.
 *
 * The open flag lives here rather than in the host because the flow itself
 * closes the sheet — on hand-over and on a completed address — and a host that
 * owned the flag would have to lend it back.
 */
export function useSignupSheet({
  surface,
  initiallyOpen,
  onExistingAccount,
  returnTo,
}: {
  surface: SignupSurface;
  initiallyOpen: boolean;
  onExistingAccount: (email: string) => void;
  returnTo: string | null;
}): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  isSigningIn: boolean;
  handleOAuth: (provider: "apple" | "google") => void;
  handleEmailSubmit: (email: string) => void;
} {
  const history = useHistory();
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(initiallyOpen);
  const [isSigningIn, setSigningIn] = useState(false);

  const open = useCallback(() => {
    setOpen(true);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOAuth = useCallback(
    (provider: "apple" | "google") => {
      if (isSigningIn) return;
      gaEventAuthMethodSelected({
        method: provider,
        intent: "signup",
        surface,
      });
      setSigningIn(true);
      // NOTE: gaEventStartSignUp is fired from OnboardingPage once we know this
      // OAuth flow produced a genuinely new / incomplete account — firing here
      // would also count returning users signing in via OAuth.
      void (async () => {
        try {
          switch (provider) {
            case "apple": {
              await loginWithApple({
                surface,
                intent: "signup",
                returnTo,
              });
              return;
            }
            case "google": {
              await loginWithGoogle({
                surface,
                intent: "signup",
                returnTo,
              });
              return;
            }
            default: {
              throw new Error(`Unknown provider: ${provider satisfies never}`);
            }
          }
        } finally {
          setSigningIn(false);
        }
      })();
    },
    [isSigningIn, surface, returnTo]
  );

  const handleEmailSubmit = useCallback(
    (email: string) => {
      if (isSigningIn) return;
      gaEventAuthMethodSelected({ method: "email", intent: "signup", surface });
      setSigningIn(true);
      void (async () => {
        try {
          const outcome = await classifyEmailForAuth(email);
          switch (outcome.kind) {
            case "invalid": {
              displayToast(t("Please input a valid email address."), "warning");
              return;
            }
            case "failed": {
              displayToast(
                t("Something went wrong. Please try again."),
                "error"
              );
              return;
            }
            case "existing": {
              displayToast(
                t(
                  "📬 An account with this email already exists. Please log in."
                ),
                "error"
              );
              setOpen(false);
              onExistingAccount(email.trim());
              return;
            }
            case "new": {
              setOpen(false);
              if (returnTo !== null) memorizePostLoginRedirectTo(returnTo);
              history.push({
                pathname: "/signup/onboarding",
                state: { email: email.trim() },
              });
              return;
            }
            default: {
              throw new Error(outcome satisfies never);
            }
          }
        } finally {
          setSigningIn(false);
        }
      })();
    },
    [isSigningIn, surface, onExistingAccount, history, t, returnTo]
  );

  return { isOpen, open, close, isSigningIn, handleOAuth, handleEmailSubmit };
}
